import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";

export interface AuditorInvitationRequest {
  id: string;
  auditor_group_id: string;
  requested_by: string;
  auditor_email: string;
  auditor_name: string;
  auditor_title: string;
  auditor_phone: string | null;
  status: "pending" | "approved" | "denied";
  reviewed_by: string | null;
  reviewed_at: string | null;
  denial_reason: string | null;
  invitation_sent_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * F-085: approved requests mint a token-based invitation (the same
   * mechanism as the auditor-manager invite) instead of a Cognito account
   * with an emailed temporary password. `invitation_token` is null for
   * pending/denied requests and for old-style auditors approved before this
   * fix (Cognito account created directly, no token ever minted).
   */
  invitation_token: string | null;
  invitation_expires_at: string | null;
  /** null when there is no minted invitation to speak of (see above). */
  invitation_accepted: boolean | null;
  auditor_group?: {
    id: string;
    name: string;
    organization_type: string;
  };
  requester?: {
    id: string;
    email: string;
  };
  reviewer?: {
    id: string;
    email: string;
  };
}

export interface CreateAuditorInvitationRequestData {
  auditors: {
    email: string;
    name: string;
    title: string;
    phone?: string;
  }[];
}

/**
 * F-086 (auditor-direct-invite, narrowed): each auditor in the batch is
 * minted independently, so the response reports BOTH what succeeded
 * (`invitations`, one per minted invite, each with its own copyable link)
 * and what didn't (`conflicts` — an email already belongs to an existing
 * employee) rather than failing the whole batch for one bad row.
 */
export interface CreateAuditorInvitationsResult {
  invitations: {
    request_id: string;
    email: string;
    name: string;
    expires_at: string | null;
    invitation_link: string;
  }[];
  conflicts: { email: string; reason: string }[];
  count: number;
  message: string;
}

export interface DenyInvitationRequestData {
  denial_reason: string;
}

/**
 * Fetch all auditor invitation requests (admin only)
 * Optional filters: status, auditor_group_id
 */
export function useAdminAuditorInvitationRequests(filters?: {
  status?: "pending" | "approved" | "denied";
  auditor_group_id?: string;
  enabled?: boolean;
}) {
  const { enabled = true, ...queryFilters } = filters ?? {};
  const queryParams = new URLSearchParams();
  if (queryFilters.status) queryParams.set("status", queryFilters.status);
  if (queryFilters.auditor_group_id)
    queryParams.set("auditor_group_id", queryFilters.auditor_group_id);

  return useQuery({
    queryKey: ["admin", "auditor-invitation-requests", queryFilters],
    queryFn: async () => {
      const { data } = await api.get(
        `/admin/auditor-invitation-requests?${queryParams.toString()}`,
      );
      return data.data as AuditorInvitationRequest[];
    },
    enabled,
  });
}

/**
 * Fetch auditor invitation requests for current auditor group manager
 */
export function useAuditorInvitationRequests() {
  return useQuery({
    queryKey: ["auditor-groups", "invitation-requests"],
    queryFn: async () => {
      const { data } = await api.get("/auditor-groups/invitation-requests");
      return data.data as AuditorInvitationRequest[];
    },
  });
}

/**
 * Create bulk auditor invitation requests (auditor group manager only)
 */
export function useCreateAuditorInvitationRequests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      requestData: CreateAuditorInvitationRequestData,
    ): Promise<CreateAuditorInvitationsResult> => {
      const { data } = await api.post(
        "/auditor-groups/invitation-requests",
        requestData,
      );
      return data.data as CreateAuditorInvitationsResult;
    },
    // US-081: InviteAuditors.tsx owns error UX via a bespoke catch toast.
    // Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["auditor-groups", "invitation-requests"],
      });
    },
  });
}

/**
 * Approve auditor invitation request (admin only)
 */
export function useApproveAuditorInvitationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; AuditorInvitationRequestsPage/Tab catch is sole caller.
    meta: { errorFallback: "Failed to approve request" },
    mutationFn: async (requestId: string) => {
      const { data } = await api.put(
        `/admin/auditor-invitation-requests/${requestId}/approve`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "auditor-invitation-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["auditor-groups", "invitation-requests"],
      });
    },
  });
}

/**
 * Deny auditor invitation request (admin only)
 */
export function useDenyAuditorInvitationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; AuditorInvitationRequestsPage/Tab catch is sole caller.
    meta: { errorFallback: "Failed to deny request" },
    mutationFn: async ({
      requestId,
      ...denyData
    }: DenyInvitationRequestData & { requestId: string }) => {
      const { data } = await api.put(
        `/admin/auditor-invitation-requests/${requestId}/deny`,
        denyData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "auditor-invitation-requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["auditor-groups", "invitation-requests"],
      });
    },
  });
}
